const API_BASE = (import.meta.env.VITE_API_URL || '') + '/api/v1';

interface ApiOptions extends RequestInit {
  token?: string;
}

async function apiClient<T>(path: string, options: ApiOptions = {}): Promise<T> {
  const { token, ...fetchOptions } = options;
  const authToken = token || localStorage.getItem('crm_token');
  
  const res = await fetch(`${API_BASE}${path}`, {
    ...fetchOptions,
    headers: {
      'Content-Type': 'application/json',
      ...(authToken && { 'Authorization': `Bearer ${authToken}` }),
      ...fetchOptions.headers,
    },
  });
  
  if (!res.ok) {
    const error = await res.json().catch(() => ({ error: 'Error de red' }));
    throw new Error(error.error || `Error ${res.status}`);
  }
  
  return res.json();
}

// Tipos
export interface Contact {
  id: number;
  name: string;
  company: string | null;
  amazon_store_url: string | null;
  email: string | null;
  phone: string | null;
  channel: string | null;
  status: string;
  source_notes: string | null;
  created_at: string;
  updated_at: string;
}

export interface ContactDetail extends Contact {
  interactions: Interaction[];
  services: Service[];
  pendingTasks: Task[];
}

export interface Interaction {
  id: number;
  contact_id: number;
  type: string;
  summary: string | null;
  next_action: string | null;
  next_action_date: string | null;
  created_at: string;
}

export interface Service {
  id: number;
  contact_id: number;
  service_type: string;
  status: string;
  monthly_fee: number | null;
  start_date: string | null;
  end_date: string | null;
  notes: string | null;
}

export interface Task {
  id: number;
  contact_id: number | null;
  contact_name: string | null;
  title: string;
  due_date: string | null;
  completed: number;
  created_at: string;
}

export interface DashboardSummary {
  funnel: {
    leads: number;
    contactados: number;
    enNegociacion: number;
    clientes: number;
    perdidos: number;
    total: number;
  };
  conversionRate: string;
  tasks: {
    today: number;
    overdue: number;
  };
  upcomingTasks: Task[];
  recentContacts: Contact[];
}

export interface CreateContact {
  name: string;
  company?: string;
  amazonStoreUrl?: string;
  email?: string;
  phone?: string;
  channel?: 'email' | 'dm' | 'llamada' | 'referido';
  status?: string;
  sourceNotes?: string;
}

export interface CreateInteraction {
  type: 'email_enviado' | 'dm_enviado' | 'llamada' | 'reunion' | 'nota';
  summary: string;
  nextAction?: string;
  nextActionDate?: string;
}

export interface CreateTask {
  contactId?: number;
  title: string;
  dueDate?: string;
}

export const api = {
  // Auth
  auth: {
    login: (email: string, password: string) =>
      apiClient<{ token: string; user: { id: number; email: string } }>('/auth/login', {
        method: 'POST',
        body: JSON.stringify({ email, password }),
      }),
    register: (email: string, password: string) =>
      apiClient<{ token: string; user: { id: number; email: string } }>('/auth/register', {
        method: 'POST',
        body: JSON.stringify({ email, password }),
      }),
  },
  
  // Contacts
  contacts: {
    list: (params?: { status?: string; channel?: string; search?: string }) => {
      const query = new URLSearchParams();
      if (params?.status) query.set('status', params.status);
      if (params?.channel) query.set('channel', params.channel);
      if (params?.search) query.set('search', params.search);
      return apiClient<{ data: Contact[] }>(`/contacts?${query.toString()}`);
    },
    get: (id: number) => apiClient<{ data: ContactDetail }>(`/contacts/${id}`),
    create: (data: CreateContact) =>
      apiClient<{ id: number; message: string }>('/contacts', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    update: (id: number, data: Partial<CreateContact>) =>
      apiClient<{ message: string }>(`/contacts/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),
    delete: (id: number) =>
      apiClient<{ message: string }>(`/contacts/${id}`, {
        method: 'DELETE',
      }),
  },
  
  // Interactions
  interactions: {
    list: (contactId: number) =>
      apiClient<{ data: Interaction[] }>(`/contacts/${contactId}/interactions`),
    create: (contactId: number, data: CreateInteraction) =>
      apiClient<{ id: number; message: string }>(`/contacts/${contactId}/interactions`, {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    update: (contactId: number, interactionId: number, data: Partial<CreateInteraction>) =>
      apiClient<{ message: string }>(`/contacts/${contactId}/interactions/${interactionId}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),
    delete: (contactId: number, interactionId: number) =>
      apiClient<{ message: string }>(`/contacts/${contactId}/interactions/${interactionId}`, {
        method: 'DELETE',
      }),
  },
  
  // Services
  services: {
    create: (contactId: number, data: { serviceType: string; status?: string; monthlyFee?: number; startDate?: string; endDate?: string; notes?: string }) =>
      apiClient<{ id: number; message: string }>(`/contacts/${contactId}/services`, {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    update: (contactId: number, serviceId: number, data: { status?: string; monthlyFee?: number; notes?: string }) =>
      apiClient<{ message: string }>(`/contacts/${contactId}/services/${serviceId}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),
    delete: (contactId: number, serviceId: number) =>
      apiClient<{ message: string }>(`/contacts/${contactId}/services/${serviceId}`, {
        method: 'DELETE',
      }),
  },
  
  // Tasks
  tasks: {
    list: (params?: { due?: string; completed?: number; contactId?: number }) => {
      const query = new URLSearchParams();
      if (params?.due) query.set('due', params.due);
      if (params?.completed !== undefined) query.set('completed', params.completed.toString());
      if (params?.contactId) query.set('contact_id', params.contactId.toString());
      return apiClient<{ data: Task[] }>(`/tasks?${query.toString()}`);
    },
    create: (data: CreateTask) =>
      apiClient<{ id: number; message: string }>('/tasks', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    update: (id: number, data: { title?: string; dueDate?: string | null; completed?: number }) =>
      apiClient<{ message: string }>(`/tasks/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),
    complete: (id: number) =>
      apiClient<{ message: string }>(`/tasks/${id}`, {
        method: 'PATCH',
        body: JSON.stringify({ completed: 1 }),
      }),
    delete: (id: number) =>
      apiClient<{ message: string }>(`/tasks/${id}`, {
        method: 'DELETE',
      }),
  },
  
  // Dashboard
  dashboard: {
    summary: () => apiClient<{ data: DashboardSummary }>('/dashboard/summary'),
  },
};
